import apiTranslations from './de.api.ts';
import primeVueTranslations from './de.primevue.ts';
import {
  generalTerm,
  generalTermFilter,
} from './de.shared.ts';

import npm from '@/generated/npm/locales/de.npm.ts';
import {
  ERouteName,
} from '@/router/route-name.ts';
import {
  EAttributeLabel,
} from '@/types/attribute-type.ts';
import {
  EWeekday,
} from '@/types/calendar.ts';
import {
  EMonth,
} from '@/types/date.ts';
import {
  ETimeseriesMeaning,
} from '@/types/openapi-type';
import {
  EVisualisation,
} from '@/types/performance-dashboard.ts';
import {
  TUnitSystem,
} from '@/types/unit-of-measurement.ts';
import {
  getComponentScopedTranslationMap,
} from '@/utils/i18n.ts';

const Visualisation: Record<EVisualisation, string> = {
  [EVisualisation.CLUSTERED_STACKED_BAR_CHART]: 'Gruppiertes und Gestapeltes Balkendiagramm',
  [EVisualisation.PARETO_CHART]: 'Pareto-Diagramm',
  [EVisualisation.STACKED_BAR_CHART]: 'Gestapeltes Balkendiagramm',
  [EVisualisation.STACKED_POLAR_CHART]: 'Gestapeltes Polardiagramm',
  [EVisualisation.TABLE]: 'Tabelle',
};

const unitSystem: Record<TUnitSystem, string> = {
  metric: 'Metrisches System',
  imperial: 'Imperiales System',
};

export default {
  generalTermFilter,
  generalTerm,
  api: apiTranslations,
  primeVue: primeVueTranslations,
  npm,
  actions: {
    clear: 'Leeren',
    accept: 'Akzeptieren',
    login: 'Login',
    reset: 'Zurücksetzen',
    search: 'Suchen',
    select: 'Auswählen',
    unlink: 'Verknüpfung aufheben',
  },
  alerts: {
    saveFailed: 'Speichern fehlgeschlagen',
    deleteSuccess: 'Erfolgreich gelöscht',
    valueAboveMax: 'Der Wert muss kleiner als {value} sein.',
  },
  assetManagement: {
    confirmResurrection: {
      header: '"{item}" wieder in Betrieb nehmen?',
      message: 'Die stillgelegte Anlage wird wieder in Betrieb genommen.',
    },
    confirmDelete: {
      header: '"{item}" löschen oder stilllegen?',
      message: 'Anlagen mit Daten, auch in untergeordneten Anlagen, werden stattdessen stillgelegt und können später wieder in Betrieb genommen werden.',
    },
    includeRetiredAssets: 'Stillgelegte Anlagen anzeigen',
    resurrectAsset: 'Wieder in Betrieb nehmen',
  },
  buttons: {
    acknowledge: 'Quittieren',
    analogInputs: 'analogeEingänge',
    cancel: 'Abbrechen',
    digitalInputs: 'Digitale Eingänge',
    editOverview: 'Übersicht bearbeiten',
    loadList: 'Produktionsliste',
    upload: 'Hochladen',
  },
  calendar: {
    occurs: 'Erfolgt',
    on: 'Ein',
    startingOn: 'beginnend am',
  },
  charts: {
    position: {
      left: 'links',
      right: 'rechts',
    },
    newAxis: 'Neue Achse',
    noAlarms: 'Keine Störungen gefunden',
  },
  fullscreen: {
    exitFullscreen: 'Vollbildmodus beenden',
    fullscreen: 'Vollbild',
  },
  measurementUnit: {
    noGroup: 'Andere',
  },
  attributeUnit: {
    [EAttributeLabel.WEIGHT]: 'Gewicht',
    [EAttributeLabel.LENGTH]: 'Länge',
    [EAttributeLabel.DIAMETER]: 'Durchmesser',
    [EAttributeLabel.SURFACE_HARDNESS]: 'Oberflächehärte',
    [EAttributeLabel.SURFACE_HARDNESS_MIN]: 'Minimale Oberflächehärte',
    [EAttributeLabel.SURFACE_HARDNESS_MAX]: 'Maximale Oberflächehärte',
    [EAttributeLabel.CORE_HARDNESS]: 'Kernhärte',
    [EAttributeLabel.CORE_HARDNESS_MIN]: 'Minimale Kernhärte',
    [EAttributeLabel.CORE_HARDNESS_MAX]: 'Maximale Kernhärte',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_MIN]: 'EGT min',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_MAX]: 'EGT max',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_HARDNESS]: 'EGT Härte',
    neuAttributePlaceholder: 'Attribut Label',
  },
  equipmentGasName: {
    [ETimeseriesMeaning.AIR_FLOW_SETPOINT]: 'Luft',
    [ETimeseriesMeaning.HYDROGEN_H2_FLOW_SETPOINT]: 'Wasserstoff',
    [ETimeseriesMeaning.NITROGEN_FLOW_SETPOINT]: 'Stickstoff',
    [ETimeseriesMeaning.OXYGEN_O2_FLOW_SETPOINT]: 'Sauerstoff',
    [ETimeseriesMeaning.AMMONIA_NH3_FLOW_SETPOINT]: 'Ammoniak',
    [ETimeseriesMeaning.DISSOCIATED_AMMONIA_NH3_FLOW_SETPOINT]: 'Dissoziiertes Ammoniak',
    [ETimeseriesMeaning.CARBON_DIOXIDE_CO2_FLOW_SETPOINT]: 'CO2',
    [ETimeseriesMeaning.CARBON_MONOXIDE_CO_FLOW_SETPOINT]: 'CO',
    [ETimeseriesMeaning.METHANE_CH4_FLOW_SETPOINT]: 'Methan',
    [ETimeseriesMeaning.PROPANE_C3H8_FLOW_SETPOINT]: 'Propan',
    [ETimeseriesMeaning.NITROUS_OXIDE_N2O_FLOW_SETPOINT]: 'N2O',
    [ETimeseriesMeaning.METHANOL_CH3OH_FLOW_SETPOINT]: 'Methanol',
    [ETimeseriesMeaning.ACETYLENE_C2H2_FLOW_SETPOINT]: 'Ethylen',
    [ETimeseriesMeaning.ARGON_FLOW_SETPOINT]: 'Argon',
    [ETimeseriesMeaning.ENDO_GAS_NATURAL_FLOW_SETPOINT]: 'Endo-Gas (Erdgas)',
    [ETimeseriesMeaning.ENDO_GAS_PROPANE_FLOW_SETPOINT]: 'Endo-Gas (Propan)',
    [ETimeseriesMeaning.ETHENE_C2H4_FLOW_SETPOINT]: 'Ethen',
    [ETimeseriesMeaning.HELIUM_FLOW_SETPOINT]: 'Helium',
    [ETimeseriesMeaning.NATURAL_GAS_FLOW_SETPOINT]: 'Erdgas',
    [ETimeseriesMeaning.WATER_FLOW_SETPOINT]: 'Wasser',
  },
  // single words or small phrases WITHOUT CONTEXT and NO LINK
  common: {
    min: 'Minimum',
    max: 'Maximum',
    emptyTable: 'Keine Daten vorhanden.',
    attribute: 'Attribut',
    revealItem: '{item} anzeigen',
    hideItem: '{item} verbergen',
    numberedItem: '{item} {number}',
    valueRange: 'Wertebereich',
    all: 'Alle',
    user: 'Benutzer',
    date: 'Datum',
    actions: 'Aktionen',
    yes: 'Ja',
    no: 'Nein',
    clear: 'Leeren',
    delete: 'Löschen',
    discard: 'Verwerfen',
    keepEditing: 'Weiter bearbeiten',
    save: 'Speichern',
    duration: 'Dauer',
    download: '{item} herunterladen',
    comment: 'Kommentar',
    print: 'Drucken',
    chart: 'Diagram',
    description: generalTerm.description,
    status: 'Status',
    assignee: 'Beauftragter',
    temperature: 'Temperatur',
    name: 'Name',
    filters: 'Filter',
    overview: 'Übersicht',
    type: 'Typ',
    set: 'Setzen',
    username: 'Benutzername',
    password: 'Passwort',
    include: 'Einbeziehen',
    exclude: 'Ausschließen',
    custom: 'Benutzerdefiniert',
    loading: 'Laden',
    retired: 'Stillgelegt',
    carbon: 'Kohlenstoff',
    enterForNewRow: 'Drücken Sie "Enter", um eine neue Zeile hinzuzufügen',
    now: 'Jetzt',
    note: 'Notiz',
    target: 'Ziel',
    source: 'Quelle',
    skip: 'Überspringen',
    reject: 'Ablehnen',
    tag: 'Schlagwort | Schlagwörter',
    withoutTag: 'Ohne Schlagwort',
    maintenance: 'Wartung',
    filter: 'Filter | Filter',
    navigation: 'Navigation',
    calendar: 'Kalender',
    noAccessToAction: 'Du hast keinen Zugriff auf diese Aktion.',
    tiny: 'Klein',
    large: 'Groß',
    size: 'Größe',
    appearance: 'Erscheinungsbild',
    notification: 'Meldung | Meldungen',
    meaning: 'Bedeutung | Bedeutungen',
    result: 'Ergebnis',
    toggle: 'Umschalten',
    statistics: 'Statistik',
    rating: 'Bewertung',
    showTable: 'Tabelle anzeigen',
    showChart: 'Diagramm anzeigen',
    configuration: 'Konfiguration',
    passed: 'Bestanden',
    failed: 'Nicht Bestanden',
    section: 'Sektion',
    capacity: 'Kapazität | Kapazitäten',
    gas: 'Gas | Gase',
    capability: 'Fähigkeit | Fähigkeiten',
    certificate: 'Zertifikat | Zertifikate',
    model: 'Modell | Modelle',
    createItem: 'Erstelle {item}',
    legend: 'Legende',
    linkItem: 'Verknüpfe {item}',
    today: 'Heute',
    earlier: 'Früher',
    addItem: '{item} hinzufügen',
    image: 'Bild',
    file: 'Datei',
    pasteFromClipboard: 'Aus Zwischenablage einfügen',
    noItemInClipboard: 'Keine {item} in der Zwischenablage',
    assetNavigation: 'Anlagennavigation',
    noRecipeSelected: 'Kein Rezept ausgewählt',
    addAssessment: 'Bewertung hinzufügen',
    addRow: 'Zeile hinzufügen',
    setAll: 'Alle setzen',
    resetAll: 'Alle zurücksetzen',
    unlock: 'Entsperren',
    notAvailable: 'N/A',
    stop: 'Stopp',
    loadItem: '{item} laden',
    add: 'Hinzufügen',
    valueAtTime: '{value} bei {time}',
    timeline: 'Zeitachse',
    Ferrite: 'Ferrit',
    Austenite: 'Austenit',
    Martensite: 'Martensit',
    labelInUnit: '{label} in {unit}',
    default: 'Voreinstellung',
    settings: 'Einstellungen',
    version: 'Version',
    logout: 'Abmelden',
    confirm: 'Bestätigen',
    edit: 'Bearbeiten',
    start: 'Starten',
    confirmUnlink: {
      header: 'Verknüpfung mit "{item}" aufheben?',
      message: 'Beide Einträge bleiben erhalten und können erneut verknüpft werden.',
    },
    confirmDelete: {
      header: '"{item}" löschen?',
      message: 'Dies kann nicht rückgängig gemacht werden.',
    },
    confirmRemove: {
      header: '"{item}" entfernen?',
    },
    takesEffectOnSave: 'Die Änderung wird beim Speichern übernommen.',
    confirmSaveChanges: {
      header: 'Änderungen an "{item}" speichern?',
      message: 'Die gespeicherte Version wird überschrieben.',
    },
    confirmChangeValue: {
      header: 'Wert auf "{value}" ändern?',
      message: 'Der aktuelle Wert ist "{currentValue}".',
    },
    remove: 'Entfernen',
    selectAll: 'Alle auswählen',
    selectItem: 'Wähle {item} aus',
    deselectAll: 'Nichts auswählen',
    value: 'Wert | Werte',
    validate: 'Überprüfen',
    selectEquipment: 'Bitte wählen Sie eine Anlagge aus der Anlagennavigation, auf der linken Seite, um Details zu sehen',
    required: 'Erforderlich',
    new: 'Neu',
    calendarWeekAbbreviation: 'KW',
    unlink: 'Trennen',
    other: 'Andere',
    cancel: 'Abbrechen',
    error: 'Fehler',
    schedule: 'Zeitplan',
    always: 'Immer',
    asset: 'Anlage',
    assets: 'Anlagen',
    severityLevels: 'Schweregrade',
    history: 'Historie',
    restore: 'Wiederherstellen',
    errorMessage: {
      errorSignalLoading: 'Fehler beim Laden von {displayName}',
      noAccess: '{entity} hat keinen "{accessType}" Zugang zu {item}',
      default: 'Es ist ein Fehler aufgetreten. Bitte laden Sie die Seite neu. Wenn der Fehler weiterhin besteht, wenden Sie sich bitte an den Support.',
      pt9800: {
        AxiosError401: 'Benutzerzuordnung nicht erfolgt oder ungültig',
        AxiosError401Item: 'Benutzerzuordnung nicht erfolgt oder ungültig für {item}',
        AxiosError403: 'Der Benutzer hat nicht die erforderlichen Berechtigungen für den Zugriff auf Protherm-Daten',
        AxiosError403Item: 'Der Benutzer hat nicht die erforderlichen Berechtigungen für den Zugriff auf Protherm-Daten für {item}',
      },
    },
    fileGeneration: {
      summary: 'Datei wird generiert',
      detail: 'Die generation von Datei kann etwas länger dauern. Bitte lassen Sie das Browserfenster offen. Diese Nachricht schließt sich automatisch nachdem der Report erstellt wurde.',
    },
    update: 'Aktualisieren',
    create: 'Erstellen',
    createCopy: 'Kopie erstellen',
    deleteItem: '{item} löschen',
    editItem: '{item} bearbeiten',
    saveItem: '{item} speichern',
    saveAsNew: 'Speichern unter ...',
    export: 'Export',
    import: 'Importieren',
    search: 'Suche',
    selected: 'Ausgewählt',
    back: 'Zurück',
    forward: 'Vorwärts',
    accept: 'Akzeptieren',
    submit: 'Bestätigen',
    success: 'Erfolg',
    close: 'Schließen',
    time: {
      second: 'Sekunde | Sekunden',
      minute: 'Minute | Minuten',
      hour: 'Stunde | Stunden',
      day: 'Tag | Tage',
      weekday: 'Wochentag | Wochentage',
      week: 'Woche | Wochen',
      month: 'Monat | Monate',
      quarter: 'Quartal | Quartale',
      year: 'Jahr | Jahre',
    },
    task: 'Charge | Chargen',
    week: {
      [EWeekday.MONDAY]: apiTranslations.Weekday.MONDAY,
      [EWeekday.TUESDAY]: apiTranslations.Weekday.TUESDAY,
      [EWeekday.WEDNESDAY]: apiTranslations.Weekday.WEDNESDAY,
      [EWeekday.THURSDAY]: apiTranslations.Weekday.THURSDAY,
      [EWeekday.FRIDAY]: apiTranslations.Weekday.FRIDAY,
      [EWeekday.SATURDAY]: apiTranslations.Weekday.SATURDAY,
      [EWeekday.SUNDAY]: apiTranslations.Weekday.SUNDAY,
    },
    month: {
      [EMonth.JANUARY]: {
        long: 'Januar',
        short: 'Jan',
      },
      [EMonth.FEBRUARY]: {
        long: 'Februar',
        short: 'Feb',
      },
      [EMonth.MARCH]: {
        long: 'März',
        short: 'Mär',
      },
      [EMonth.APRIL]: {
        long: 'April',
        short: 'Apr',
      },
      [EMonth.MAY]: {
        long: 'Mai',
        short: 'Mai',
      },
      [EMonth.JUNE]: {
        long: 'Juni',
        short: 'Jun',
      },
      [EMonth.JULY]: {
        long: 'Juli',
        short: 'Jul',
      },
      [EMonth.AUGUST]: {
        long: 'August',
        short: 'Aug',
      },
      [EMonth.SEPTEMBER]: {
        long: 'September',
        short: 'Sep',
      },
      [EMonth.OCTOBER]: {
        long: 'Oktober',
        short: 'Okt',
      },
      [EMonth.NOVEMBER]: {
        long: 'November',
        short: 'Nov',
      },
      [EMonth.DECEMBER]: {
        long: 'Dezember',
        short: 'Dez',
      },
    },
    timeAbbreviation: {
      days: 'd',
      hours: 'h',
      minutes: 'min',
      months: 'm',
      weeks: 'KW',
      years: 'a',
    },
    reset: 'Zurücksetzen',
    none: 'Nichts',
    copy: 'Kopieren',
    copyNoun: 'Kopie',
    read: 'Lesen',
    write: 'Schreiben',
  },
  component: {
    accordionAudit: {
      envelopeNamePlaceholder: 'Grenzenname',
    },
    alarmView: {
      header: 'Störungen',
      empty: 'Keine Störungen gefunden',
    },
    appAndAssetPermissionTree: {
      appPermission: 'App-Berechtigungen',
      assetPermission: 'Anlagenberechtigungen',
    },
    counterActionModal: {
      create: 'Gegenmaßnahme erstellen',
      update: 'Gegenmaßnahme aktualisieren',
    },
    counterActionTable: {
      create: 'Gegenmaßnahme erstellen',
      confirmDelete: 'Gegenmaßnahme "{counterActionName}" löschen?',
      confirmUnlink: 'Ursache "{rootCauseName}" von Gegenmaßnahme "{counterActionName}" trennen?',
    },
    dashboardPanel: {
      add: 'Panel hinzufügen',
      defaultTitle: 'Mein Panel',
    },
    equipmentDocumentUploadModal: {
      headline: 'Dokumente hochladen',
      categoryTitle: 'Kategorie',
      descriptionTitle: 'Beschreibung',
      uploadFilesTitle: 'Datei',
      errorMessage: 'Datei existiert bereits',
    },
    expressionPreviewModal: {
      title: 'Neues virtuelles Signal',
    },
    furnaceViewRecipe: {
      stopConfirm: {
        header: 'Rezept "{0}" stoppen?',
        message: 'Das Rezept stoppt sofort.',
      },
      startConfirm: {
        header: 'Rezept "{0}" starten?',
        message: 'Das Rezept startet sofort.',
      },
      goToConfirm: {
        message: 'Das Rezept wird sofort mit diesem Schritt fortgesetzt.',
      },
    },
    loadModal: {
      startingLoad: 'Charge wird gestartet...',
      confirmPartHint: 'Bitte bestätigen Sie, dass alle Teile geladen sind',
      confirmStart: 'Bestätigen Sie den Start der Charge.',
      loadIdentifier: generalTerm.loadIdentifier,
      createOrUpdate: 'Auftrag plannen',
      description: 'Planen Sie Ihre nächste Charge, indem Sie Ihre',
      addTraveller: 'Neuen Reisenden hinzufügen',
      emptyPartsTable: 'Keine Teile hinzugefügt',
      partNotFound: 'Teil-Nr. "{id}" wurde nicht gefunden',
      partDoNotMatch: 'Nicht alle Teile entsprechen dem Rezept',
      readyToScan: 'Bereit zum Scannen',
    },
    materialSelect: {
      placeholder: 'Werkstoff auswählen',
    },
    materialGroupTable: {
      tableHeader: 'Werkstoffgruppen',
    },
    maintenanceCalendarJobModal: {
      titleCreate: 'Wartungscharge erstellen',
      titleEdit: 'Wartungscharge bearbeiten',
      createTask: 'Aufgabe erstellen',
    },
    observationView: {
      header: 'Beobachtungen',
    },
    observationTypeTable: {
      unlinkConfirm: 'Ursache "{rootCauseName}" von Beobachtungstyp "{observationTypeName}" trennen?',
    },
    PartAttribute: {
      headline: 'Teile Einstellungen',
      subheadline: 'Die in dieser Tabelle definierten Attribute werden bei der Erstellung/Änderung von Teilen abgefragt.',
    },
    productionLogsTable: {
      tab: {
        table: 'Tabelle',
        chart: 'Diagramm',
      },
    },
    qualityForm: {
      jobSelection: 'Charge auswählen',
      partSelection: 'Teil auswählen',
    },
    rootCauseLinkModal: {
      title: 'Ursache verknüpfen',
    },
    rootCauseModal: {
      create: 'Ursache erstellen',
      update: 'Ursache aktualisieren',
    },
    rootCauseGroupModal: {
      create: 'Ursachengruppe erstellen',
      update: 'Ursachengruppe aktualisieren',
    },
    rootCauseTable: {
      create: 'Ursache erstellen',
      confirmDelete: 'Ursache "{rootCauseName}" löschen?',
      confirmUnlink: 'Gegenmaßnahme "{counterActionName}" von Ursache "{rootCauseName}" trennen?',
    },
    rootCauseGroupTable: {
      create: 'Ursachengruppe erstellen',
      confirmDelete: 'Ursachengruppe "{rootCauseGroupName}" löschen?',
      confirmUnlink: 'Ursache "{rootCauseName}" von Ursachengruppe "{rootCauseGroupName}" trennen?',
    },
    TableJobStages: {
      header: 'Rezeptschritte',
    },
    treeSidebarTemplate: {
      defaultTitle: 'Asset Navigation',
    },
    userUpdateModal: {
      tab: {
        permissionList: 'Benutzerrechte',
        controllerUserMapping: 'Benutzeranlagenrechte',
      },
      permissionList: 'Benutzerrechte-Liste',
      groupList: 'Gruppenliste',
      selectPT9800: 'Protherm-Benutzer auswählen',
      enterCredentials: 'Anmeldedaten eingeben',
    },
  },
  details: {
    images: 'Bilder',
  },
  errorMessages: {
    error: 'Fehler',
    noEquipmentSelected: 'Bitte wählen Sie eine Anlage aus der linken Navigation aus, um Details zu sehen.',
    outOfRangeTemperature: 'Die Temperatur liegt außerhalb des zulässigen Bereichs: -200°C - 850°C (-328°F - 1562°F). Bitte passen Sie den RG oder RL Wert an',
  },
  forms: {
    credentials: 'Zugangsdaten',
    persistCredentials: 'Zugangsdaten speichern',
  },
  global: {
    workplace: 'Arbeitsplatz',
  },
  images: {
    noImages: 'Keine Bilder',
  },
  inputLabels: {
    from: 'von',
    to: 'bis',
  },
  materialComposition: {
    min: {
      label: 'Min',
      description: 'Analyse mit den kleinsten Werkstoffeigenschaften.',
    },
    max: {
      label: 'Max',
      description: 'Analyse mit den größten Werkstoffeigenschaften.',
    },
    avg: {
      label: 'Mittelwert',
      description: 'Analyse mit mittleren Werkstoffeigenschaften.',
    },
    comp: {
      label: 'Mittlere Analyse',
      description: 'Durschnittliche Analyse entsprechend der Gewichtsprozente.',
    },
  },
  modifyMenu: {
    addNew: 'Neuer {0}',
    edit: 'Ändern',
    saveAs: 'Speichern unter',
  },
  page: {
    anomalyDetection: {
      noObservations: 'Keine Beobachtungen gefunden',
    },
    certification: {
      title: 'Audit & Quality',
      auditConfiguration: {
        deleteSuccess: 'Spezifikation wurde erfolgreich gelöscht.',
      },
    },
    openSourceLicenses: {
      title: 'Open-Source-Lizenzen',
    },
    liveApp: {
      equipment: {
        noJobs: 'Keine Chargen gefunden',
      },
    },
    notification: {
      title: 'Meldungen',
    },
    userManagement: {
      manageRole: 'Rollen verwalten',
      headline: 'Benutzer ({0})',
      customPermissions: 'Benutzerdefinierte Zugriffsrechte',
      searchPlaceholder: {
        name: 'Suche nach Name',
        email: 'Suche nach E-Mail',
        role: 'Suche nach Rolle',
      },
      showDeletedUsers: 'Gelöschte Benutzer anzeigen',
      restoreUser: 'Benutzer wiederherstellen',
      confirmRestoreUser: {
        header: '"{displayName}" wiederherstellen?',
        message: 'Der Benutzer kann sich wieder bei Qmulus anmelden.',
      },
      confirmDeleteUserMessage: 'Gelöschte Benutzer können wiederhergestellt werden.',
      deleted: 'Gelöscht',
    },
    roleManagement: {
      roles: 'Rollen',
      createRole: 'Rolle erstellen',
    },
    home: {
      title: 'Startseite',
      welcomeMorning: 'Guten Morgen, {firstName}!',
      welcomeAfternoon: 'Hallo, {firstName}!',
      welcomeEvening: 'Guten Abend, {firstName}!',
      welcomeNight: 'Noch so spät am Arbeiten, {firstName}?',
      welcomeSubtitles: [
        'Deine Öfen warten — lass uns heute einen großartigen Lauf hinlegen.',
        'Zeit, den Tag anzuheizen — deine Prozesse sind bereit.',
        'Stellen wir sicher, dass jeder Auftrag heute nach Spezifikation läuft.',
        'Behalte deine Standorte im Blick, verfolge deine Aufträge und bleib in Kontrolle.',
        'Alle deine Wärmebehandlungsabläufe, immer griffbereit.',
        'Dein Überblick über jeden Standort, jeden Ofen, jeden Auftrag.',
        'Was läuft heute? Schau in die App-Übersicht und finde es heraus.',
        'Behalte deine Chargen im Blick und sichere die Qualität.',
        'Überprüfe deine Öfen und halte deine Prozesse auf Kurs.',
      ],
    },
    appOverview: {
      title: 'App-Übersicht',
      showOnlyAvailableApps: 'Nur verfügbare Apps anzeigen',
      search: 'App suchen',
    },
    releaseNotes: {
      title: 'Versionshinweise',
    },
    releaseHighlights: {
      title: 'Neuerungen',
    },
    userManual: {
      title: 'Benutzerhandbuch',
    },
    productionStatisticsDetails: {
      panelSettings: {
        header: 'Panel Einstellungen',
        helpText: 'Wähle einen Titel für das Panel und die KPIs, die angezeigt werden sollen. Die Art des Diagramms kann im Panel ausgewählt werden.',
        labelTitle: 'Titel',
        labelSize: 'Größe',
        labelType: 'Typ',
        labelKpiSelection: 'KPI Auswahl',
      },
      size: {
        medium: 'Mittel',
        large: 'Groß',
      },
      type: {
        table: 'Tabelle',
        totals: 'Gesamtsummen',
        totalDistribution: 'Gesamtverteilung',
        childrenTotals: 'Kind-Gesamtsummen',
        childrenDistribution: 'Kind-Verteilung',
        childrenComparison: 'Kind-Vergleich',
      },
    },
    assetManagementEquipmentSensorMapping: {
      mappedSignalTitle: 'Abgebildetes Signal',
    },
    assetManagement: {
      overview: {
        title: 'Anlagenübersicht',
        tableHeader: 'Anlagenbaum',
        filter: {
          name: 'Nach Name filtern',
        },
      },
      equipmentTab: {
        [ERouteName.ASSET_MANAGEMENT_ALARM_MANAGEMENT]: 'Störungsverwaltung',
        [ERouteName.ASSET_MANAGEMENT_CAMERAS]: 'Kameras',
        [ERouteName.ASSET_MANAGEMENT_DATA_SOURCES]: 'Datenquellen',
        [ERouteName.ASSET_MANAGEMENT_DOCUMENT]: 'Dokumente',
        [ERouteName.ASSET_MANAGEMENT_MAIN]: 'Übersicht',
        [ERouteName.ASSET_MANAGEMENT_PARAMETER]: 'Parameter',
        [ERouteName.ASSET_MANAGEMENT_SENSOR_MAPPING]: 'Sensorzuordnung',
        [ERouteName.ASSET_MANAGEMENT_SENSOR_SETTINGS]: 'Sensor-Einstellungen',
      },
    },
    pt9800: {
      common: {
        id: 'Id',
        name: 'Name',
        description: 'Beschreibung',
        modifiedBy: 'Geändert von',
        modifiedOn: 'Letze Änderung',
        number: 'Nummer',
        comment: 'Bemerkung',
      },
      confirmDeleteHeader: '{0} "{1}" löschen?',
      error: {
        create: '{0} konnte nicht erstellt werden',
        update: '{0} konnte nicht bearbeitet werden',
        delete: '{0} konnte nicht gelöscht werden',
        clone: '{0} konnte nicht kopiert werden',
      },
      form: {
        header: {
          create: '{0} erstellen',
          edit: '{0} bearbeiten',
          delete: '{0} löschen',
        },
      },
      entryType: {
        alarm: 'Störung',
        application: 'Programm',
        applicationGroup: 'Programmgruppe',
        box: 'Box',
        clientAccess: 'Rechte-Client',
        color: 'Farbe',
        terminal: 'Client',
        terminalType: 'Clienttyp',
        user: 'Benutzer',
        userAccess: 'Rechte pro Benutzer',
        userGroup: 'Benutzergruppe',
        backgroundProcess: 'Hintergrundprozess',
        tasks: 'Charge',
        workplace: 'Arbeitsplatz',
        workplaceType: 'Arbeitsplatz-Typ',
        operation: 'Vorgang',
      },
      backgroundProcess: {
        order: 'Auftrag',
        parameter: 'Parameter',
        active: 'Aktiv',
        activeTime: 'Aktiv Zeit',
        pid: 'PID',
        header: 'Hintergrundprozess',
      },
      operation: {
        header: 'Vorgang',
        property: 'Eigenschaft',
        operationType: 'Vorgangsart',
        type: 'Typ',
        mode: 'Modus',
        order: 'Auftrag',
        peProcessType: 'PE-Prozesstyp',
        sortingNo: 'Sortierung-Nr.',
        automatingPlanningMode: 'Automatischer Planungsmodus',
        relevantForProductionStatistics: 'Relevant für die Produktionsstatistik',
        relevantForStat: 'Relevant für Statistik',
        selectProcess: 'Prozess auswählen',
        selectPart: 'Teil auswählen',
        sapNo: 'Sap-Nr.',
        workplaceName: 'Arbeitsplatz/ WP-Gruppen',
        placeDescription: 'Ortsbeschreibung',
      },
      tasks: {
        header: 'Verwaltung der Charge',
        active: 'Aktiv',
        type: 'Typ',
        command1: 'Befehl 1',
        command2: 'Befehl 2',
        command3: 'Befehl 3',
        command4: 'Befehl 4',
        command5: 'Befehl 5',
        command6: 'Befehl 6',
        command7: 'Befehl 7',
        command8: 'Befehl 8',
        command9: 'Befehl 9',
        command10: 'Befehl 10',
        startHour: 'Start Stunde',
        dateLastActive: 'Datum zuletzt aktiv',
        intervalInMin: 'Intervall in min [min]',
      },
      workplace: {
        logicTypes: 'Logiktypen',
        header: 'Arbeitsplatz',
        inventoryNumber: 'Ausrüstung-Nr.',
        belongsTo: 'Gehört zu Arbeitsplatz-Id',
        ip: 'IP-Adresse',
        mGuardIp: 'MGuard IP',
        device: 'Gerät',
        telegramSplit: 'RFC-Telegramm-Split',
        deactivateOperations: 'Vorgang deaktivieren',
        aliveBitErrorNum: 'Alive-Bit-Fehlernummer',
        logicType: 'Logiktyp',
        recipePrefix: 'Rezept-Präfix',
        chamberId: 'Kammer-Id',
        sharesController: 'Teilt Controller/Kopie von Kammer-Id',
        mainUseMode: 'Hauptverwendungsmodus',
        workplaceType: 'Arbeitsplatztyp',
        isActive: 'Ist aktiv',
        importantSettings: 'Wichtige Einstellungen',
      },
      alarm: {
        alarmAdministration: 'Störung',
        alarm: 'Störungs-Nr.',
        alarmText: 'Störungstext',
        causesTestingLoad: 'Wird zu Prüfcharge',
        onlyForChamber: 'Nur für Kammer',
        security: 'Pförtner',
        color: 'Farbe',
        errorType: 'Störungstyp',
        outputType: 'Ausgabe-Typ',
        alarmGroup: 'Störungsgruppe',
      },
      application: {
        header: 'Programm',
        command: 'Befehl',
        groupId: 'Gruppen-Id',
        iconGifName: 'Icon-Gif-Name',
        furnacePicture: 'Ofenbilder',
        order: 'Auftrag',
        parameter: 'Parameter',
        tooltip: 'Tooltip',
        typeId: 'Typ',
      },
      applicationGroup: {
        header: 'Programmgruppe',
      },
      systemData: {
        active: 'Aktiv',
        addresseeMobilePhoneNo: 'Telefon-Nr. des Empfängers',
        boxHeader: 'Verwaltung',
        colorHeader: 'Farbverwaltung',
        colorValue: 'Farbwert',
        pin: 'PIN',
        serialInterface: 'Serielle Schnittstelle',
        smsHeader: 'SMS',
        testMessage: 'Test message (1=YES)',
        used: 'Benutzt',
      },
      terminal: {
        header: 'Client',
        id: 'Id',
        clientType: 'Clienttyp',
        name: 'Name',
        osType: 'Betriebssystem:',
        remark: 'Bemerkung',
        address: 'Adresse',
        description: 'Beschreibung',
        location: 'Aufstellungs-Ort:',
        valueLong2: '',
        valueLong3: 'Integer Wert 3:',
        valueLong4: 'Integer Wert 4:',
        valueLong5: 'Integer Wert 5:',
        valueChar1: 'Character Wert 1:',
        valueChar2: 'Character Wert 2:',
        actUserName: 'Aktueller Benutzer:',
        actLoginTime: 'Aktuelle Login Zeit',
        terminalNo: 'Client-No:',
        lastLoginTime: 'Letzte Login Zeit:',
        lastUserName: 'Letzter Benutzer',
        resolution: 'Bildschirmauflösung',
        permission: 'Erlaubnis',
        rightsToTerminals: 'Rechte auf Clients',
      },
      terminalType: {
        header: 'Client-Typ',
      },
      user: {
        header: 'Benutzer',
        accessLevel: 'Benutzerrecht',
        comment: 'Bemerkung',
        costCenter: 'Kostenstelle',
        employeeNumber: 'Personal-Nr.',
        shortName: 'Kurzname',
        firstName: 'Vorname',
        function: 'Funktion',
        lastName: 'Nachname',
        language: 'Sprache',
        loginTimeInMin: 'Login-Zeit in min."',
        maintenancePopups: 'Wartungsplan Popups',
        password: 'Passwort',
        plant: 'Werk',
        playAlarmSound: 'Bei Störungen Signalton abspielen',
        userGroup: 'Benutzergruppe',
      },
      userAccess: {
        header: 'Rechte pro Benutzer',
        delete: 'Löschen',
        newEntry: 'Neuer Eintrag',
        print: 'Drucken',
        run: 'Ausführen',
        save: 'Speichern',
        special1: 'Special-1',
        startRecipeProcessFlow: 'Start Beh/Ablauf',
        modifyRecipeProcessFlow: 'Eingriff Beh/Ablauf',
        startCorrectionRecipe: 'Korrekturprogr. Starten',
        stopRecipeProcessFlow: 'Stop Beh/Ablauf',
        selectUser: 'Benutzer auswählen',
      },
      userGroup: {
        header: 'Benutzergruppe',
      },
      workplaceType: {
        header: 'Arbeitsplatz-Typ',
        prio: 'Prio',
      },
    },
    login: {
      title: 'Login',
    },
    anomalyDetectionJobFeatureDetail: {
      tab: {
        observationTable: 'Beobachtungen',
        rootCauseTable: 'Ursachen',
      },
    },
    anomalyDetectionEquipmentSettings: {
      title: 'Einstellungen',
      tab: {
        counterActionTable: 'Gegenmaßnahmen',
        rootCauseGroupTable: 'Ursachen-Gruppen',
        rootCauseTable: 'Ursachen',
        jobFeatureTable: 'Chargenfunktionen',
      },
    },
    anomalyDetectionEquipment: {
      tab: {
        rootCauseTable: 'Möglichen Ursachen',
        jobFeatureTable: 'Beobachtungen pro Chargenbeschreibung',
      },
      jobFeatureTable: {
        header: {
          jobFeatureName: 'Chargenbeschreibung',
          observationCount: 'Anzahl der Beobachtungen',
          rootCauseCount: 'Anzahl der Ursachen',
        },
      },
      observationTypeByJobFeatureTable: {
        header: {
          observationTypeName: 'Name des Beobachtungstyps',
          observationCount: 'Anzahl der Beobachtungen',
          rootCauseIdList: 'Anzahl der Ursachen',
        },
      },
    },
    quality: {
      header: {
        title: 'Qualität',
        createReport: 'Bericht erstellen',
      },
    },
    auditResult: {
      asset: 'Anlage',
      userOverruledReason: 'Begründung für die Ergebniskorrektur',
      duration: 'Dauer',
      endTime: 'Endzeitpunkt',
      headerTitle: 'Auditbericht',
      loadNo: 'Chargen-Nr.',
      location: 'Standort',
      rating: 'Bewertung',
      recipe: 'Rezept',
      reportNo: 'Bericht-Nr.',
      reporter: 'Prüfer',
      signature: {
        customer: 'Kunde',
        engineering: 'Ingenieurswesen',
        manufacturing: 'Fertigung',
        material: 'Material',
        program: 'Programm',
        quality: 'Qualitätssicherung',
      },
      startTime: 'Startzeit',
    },
    productionLogList: {
      compare: 'Vergleiche',
      export: 'Export',
    },
  },
  recipeBuilder: {
    alarmDeactivated: 'Störung deaktiviert',
    alarmDelay: 'Störungsverzögerung',
    equi: 'Equi',
    highAlarmOffsets: 'HIGH Alarm Offsets',
    highWarningOffsets: 'HIGH Warnung Offsets',
    lowAlarmOffsets: 'LOW Alarm Offsets',
    lowWarningOffsets: 'LOW Warnung Offsets',
    referenceValue: 'Referenzwert',
    setAlarmLimit: 'Störungsgrenze einstellen',
    setAlarmLimits: 'Störungsgrenzen festlegen',
    setValue: 'Wert einstellen',
  },
  recipeOnDemand: {
    chart: {
      gasCompositionLabel: {
        ammonia: 'Ammoniak: {value} %',
        dissociatedAmmonia: 'Dissoziiertes Ammoniak: {value} %',
        nitrogen: 'Stickstoff: {value} %',
        carbonOxide: 'Kohlenmonoxid: {value} %',
        carbonDioxide: 'Kohlendioxid: {value} %',
      },
      processLabel: {
        knAct: 'Kn Act: {value}',
      },
    },
    compoundLayerThickness: 'Dicke der Verbundschicht',
    coreHardnessHV: 'Kernhärte (HV)',
    diffusionLayerThickness: 'Dicke der Diffusionsschicht',
    distance: 'Distanz',
    effective: 'Wirksam',
    gasFlowChart: 'Gasflussdiagramm',
    hardness: 'Härte',
    material: 'Werkstoff',
    nitridetHardness: 'Tiefe der Nitrierhärte',
    nitriding: 'Nitrieren',
    nitrocarburizing: 'Nitrocarburieren',
    oxideLayerThickness: 'Dicke der Oxidschicht',
    porosity: 'Porosität %',
    postoxidation: 'Postoxidation',
    process: 'Process',
    processChart: 'Prozessdiagramm',
    profileChart: 'Profildiagramm',
    stepNumber: 'Schritt {number}',
    surfaceHardness: 'Härte der Oberfläche',
    unit: 'Einheit',
    unitSystems: {
      imperial: 'Imperial',
      metric: 'Metrisch',
    },
    vickersHardness: 'Vickers-Härte',
  },
  recipes: {
    carburizingDepth: generalTerm.carburizingDepth,
    endTemperature: 'Endtemperatur',
    grainSize: 'ASTM-Korngrößenzahl:',
    nitridingDepth: generalTerm.nitridingDepth,
    quenchingIntensity: 'Abschreckintensität',
    representativeDiameter: 'Maßgeblicher Wärmebehandlungsdurchmesser (DIN 17211)',
    requiredHardnessHV: 'Grenzhärte:',
    noAccess: 'Benutzer hat keinen Zugriff auf {0}',
  },
  report: {
    approvals: 'Zulassungen',
    customer: 'Kunde',
    engineering: 'Technik',
    manufacturing: 'Herstellung',
    materiel: 'Materie',
    program: 'Programm',
    quality: 'Qualität',
  },
  simulator: {
    overwrite: 'Überschreiben',
  },
  tableHeaders: {
    alarmType: 'Störungstyp',
    avg: 'Durchschn. {unit}',
    description: 'Beschreibung',
    max: 'Max {unit}',
    min: 'Min {unit}',
    name: 'Name',
    value: 'Wert',
    valueWhenDeclared: 'Wert bei Auslösung',
    timeOfDeclaration: 'Zeitpunkt der Auslösung',
  },
  tabs: {
    materials: 'Werkstoffe',
    materialsGroups: 'Werkstoffgruppen',
  },
  tiles: {
    userManagement: 'Benutzerverwaltung',
    materials: 'Werkstoffdatenbank',
    part: 'Teile',
    loadingScheme: 'Beladepläne',
    productionLogs: 'Produktionshistorie',
    customer: 'Kunde',
  },
  titles: {
    alarms: 'Störungen',
    noData: 'Keine Daten vorhanden',
    settings: 'Einstellungen',
    valuesAndCalculations: 'Werte und Berechnungen',
  },
  type: {
    Visualisation,
  },
  upcPro: {
    analyzerReading: 'Analyzer Reading',
    convertsTo: 'ergibt',
    endoGeneratorDataError: 'DATEN EINGABEFEHLER:\nCO[%] + CO₂[%] + CH₄[%] + H₂[%] > 100 \nBITTE DIE WERTE KORRIGIEREN.',
    floMeterGlossary: 'Glossar:\n\nSG1 = Spezifische Dichte des Gases, für das das Durchflussmessgerät kalibriert ist\n\nSG2 = Spezifisches Gewicht des Gases, für das Flo-Meter verwendet wird\n\nTemp.1 = Temperatur, für die der Durchflussmesser kalibriert ist\n\nTemp.2 = Temperatur, für die der Durchflussmesser verwendet wird\n\nP1 = Druck, für den der Durchflussmesser kalibriert ist\n\nP2 = Druck, für den der Durchflussmesser verwendet werden soll\n\nÜbersetzt mit www.DeepL.com/Translator (kostenlose Version)',
    flows: 'Eingangsströme',
    furnaceAtmosphere: 'Ofenatmosphäre',
    imperialUnits: 'imperial',
    inputGroup: 'Input',
    inputs: {
      CELSIUS_TO_FAHRENHEIT: 'Celsius zu Fahrenheit',
      FAHRENHEIT_TO_CELSIUS: 'Fahrenheit zu Celsius',
      FT3PH_TO_M3PH: 'cfh zu m³/h',
      GPM_TO_LMIN: 'US gpm zu l/min',
      HARDNESS_TABLE1: 'Rockwell C Range',
      HARDNESS_TABLE2: 'Rockwell B Range',
      HBS: {
        name: 'HBS',
        subtitle: 'Brinell Scale',
      },
      HBS2: {
        name: 'HBS',
        subtitle: 'Brinell Scale',
      },
      HBW: {
        name: 'HBW',
        subtitle: 'Brinell Scale',
      },
      HK: {
        name: 'HK',
        subtitle: 'Knoop Hardness',
      },
      HK2: {
        name: 'HK',
        subtitle: 'Knoop Hardness',
      },
      HR15N: {
        name: 'HR15N',
        subtitle: 'Rockwell 15-N Scale',
      },
      HR15T: {
        name: 'HR15T',
        subtitle: 'Rockwell 15-T Scale',
      },
      HR30N: {
        name: 'HR30N',
        subtitle: 'Rockwell 30-N Scale',
      },
      HR30T: {
        name: 'HR30T',
        subtitle: 'Rockwell 30-T Scale',
      },
      HR45N: {
        name: 'HR45N',
        subtitle: 'Rockwell 45-N Scale',
      },
      HR45T: {
        name: 'HR45T',
        subtitle: 'Rockwell 45-T Scale',
      },
      HRA: {
        name: 'HRA',
        subtitle: 'Rockwell A Scale',
      },
      HRA2: {
        name: 'HRA',
        subtitle: 'Rockwell A Scale',
      },
      HRB: {
        name: 'HRB',
        subtitle: 'Rockwell B Hardness',
      },
      HRC: {
        name: 'HRC',
        subtitle: 'Rockwell C Hardness',
      },
      HRD: {
        name: 'HRD',
        subtitle: 'Rockwell D Scale',
      },
      HRF: {
        name: 'HRF',
        subtitle: 'Rockwell F Hardness',
      },
      HSC: {
        name: 'HSc',
        subtitle: 'HSc Scleroscope Hardness',
      },
      HV: {
        name: 'HV',
        subtitle: 'Vickers Hardness',
      },
      HV2: {
        name: 'HV',
        subtitle: 'Vickers Hardness',
      },
      LMIN_TO_GPM: 'l/min zu US gpm',
      M3PH_TO_FT3PH: 'm³/h zu cfh',
      ac: 'ac',
      actualScale: 'Actual Scale',
      aisi: 'AISI',
      al: 'Al',
      alloyFactor: 'Legierungsfaktor',
      baseC: 'Base C',
      c: 'C',
      carbonPotential: 'Carbon Potenzial',
      cch4: 'C + CH₄',
      ccontent: 'C-Content',
      ch3oh: 'CH₃OH',
      ch4: 'CH₄',
      ch4Factor: 'CH₄ Factor',
      co: 'CO',
      co2: 'CO₂',
      coTotalFlow: 'CO/Total Flow',
      coco2: 'CO/CO₂',
      coldJunctionTemp: 'Cold-Junction Temp.',
      cpatm: 'CPatm',
      cr: 'Cr',
      cu: 'Cu',
      dewPoint: 'Dew Point',
      din: 'DIN',
      dissBurette: 'Diss. Burette',
      dissDegree: 'Diss. Degree',
      dnh3: 'D-NH₃',
      dpt: 'DPT',
      emf: 'EMF',
      floMeterScale: 'Flo-Meter Scale',
      flow: 'Flow',
      gas: 'Gas',
      h2: 'H₂',
      h2n2: 'H₂:N₂',
      h2o: 'H₂O',
      hardness: 'Hardness',
      kcb: 'Kᴄʙ',
      kh: 'Kʜ',
      kn: 'Kɴ',
      ko: 'Kᴏ',
      mn: 'Mn',
      mo: 'Mo',
      mv: 'mV',
      n2: 'N₂',
      n2Percentage: 'N₂',
      n2ch3oh: 'N₂/CH₃OH',
      nc2: 'NC₂',
      nh3: 'NH₃',
      ni: 'Ni',
      o2: 'O₂',
      o2Percentage: 'O₂',
      o2Probe: 'O₂ Probe',
      others: 'Others',
      p1: 'P1',
      p2: 'P2',
      po2: 'pO₂',
      preload: 'Preload',
      pt10: {
        description: '(Pt10) Platinum RTD with Nominal Resistance of 10 Ohms at 0°C (32°F).\nRange: -200°C - 850°C (-328°F - 1562°F) as per IEC 751 / DIN EN 60751.',
        name: '(Pt10) Platinum RTD',
      },
      pt100: {
        description: '(Pt100) Platinum RTD with Nominal Resistance of 100 Ohms at 0°C (32°F).\nRange: -200°C - 850°C (-328°F - 1562°F) as per IEC 751 / DIN EN 60751.',
        name: '(Pt100) Platinum RTD',
      },
      pt1000: {
        description: '(Pt1000) Platinum RTD with Nominal Resistance of 1000 Ohms at 0°C (32°F).\nRange: -200°C - 850°C (-328°F - 1562°F) as per IEC 751 / DIN EN 60751.',
        name: '(Pt1000) Platinum RTD',
      },
      pt500: {
        description: '(Pt500) Platinum RTD with Nominal Resistance of 500 Ohms at 0°C (32°F).\nRange: -200°C - 850°C (-328°F - 1562°F) as per IEC 751 / DIN EN 60751.',
        name: '(Pt500) Platinum RTD',
      },
      resistance: 'Resistance',
      rg: 'RG',
      rl: 'RL',
      scaleFactor: 'Scale Factor',
      sg1: 'SG1',
      sg2: 'SG2',
      si: 'Si',
      sootLimit: 'Soot Limit',
      tc_b: {
        description: 'Plus:Platinum 30% Rhodium(Pt30Rh)\nMinus:Platinum 6% Rhodium (Pt6Rh)\nRange: -50°C-1820°C (-58°F-3308°F)\nColors according to ANSI 96.6.\nAccording to NFC 42-324, colors are yellow and gray',
        name: 'Type B = Pt30Rh-Pt6Rh',
      },
      tc_e: {
        description: 'Plus:Nickel-Chromium (NiCr), also\nknown as Chromel\nMinus:Copper-Nickel (CuNi), also\nknown as Constantan\nRange: -270°C-1000°C (-454°F-1832°F)\nColors according to DIN EN 60584-1',
        name: 'Type E = NiCr-CuNi',
      },
      tc_j: {
        description: 'Plus:Copper(Cu), Copper Color\nMinus:Copper-Nickel (CuNi), also\nknown as Constantan\nRange: -270°C-400°C (-454°F-752°F)\nColors according to DIN EN 60584-1',
        name: 'Type J = Fe-CuNi',
      },
      tc_k: {
        description: 'Plus:Nickel-Chromium (NiCr), also\nknown as Chromel\nMinus:Nickel Aluminum (NiAl) also\nknown as Alumel, magnetic wire\nRange: -270°C-1372°C (-454°F-2502°F)\nColors according to DIN EN 60584-1',
        name: 'Type K = NiCr-Ni',
      },
      tc_l: {
        description: 'Plus:Iron (Fe), magnetic wire\nMinus:Copper-Nicker(CuNi), also\nknown as Constantan\nRange: 0°C-900°C (32°F-1652°F)\nColors according to DIN EN 43714',
        name: 'Type L = Fe-CuNi',
      },
      tc_n: {
        description: 'Plus:Nickel-Chromium-Silicon (NiCrSi),\nalso known as Nicrosil\nMinus:Nickel-Silicon (NiSi), also\nknown as Nisil\nRange: -270°C-1300°C (-454°F-2372°F)\nColors according to DIN EN 60584-1',
        name: 'Type N = NiCrSi-NiSi',
      },
      tc_r: {
        description: 'Plus:Platinum 13% Rhodium (Pt13Rh)\nMinus:Platinum(Pt), minus wire softer\nthan plus wire\nRange: -50°C-1768°C (-58°F-3214°F)\nColors according to DIN EN 60584-1',
        name: 'Type R = Pt13Rh-Pt',
      },
      tc_s: {
        description: 'Plus:Platinum 10% Rhodium (Pt10Rh)\nMinus:Platinum(Pt), minus wire softer\nthan plus wire\nRange: -50°C-1768°C (-58°F-3214°F)\nColors according to DIN EN 60584-1',
        name: 'Type S = Pt10Rh-Pt',
      },
      tc_t: {
        description: 'Plus:Iron (Fe), magnetic wire\nMinus:Copper-Nickel (CuNi), also\nknown as Constantan\nRange: -210°C-1200°C (-346°F-2192°F)\nColors according to DIN EN 60584-1',
        name: 'Type T = Cu-CuNi',
      },
      tc_u: {
        description: 'Plus:Copper(Cu), Copper Color\nMinus:Copper-Nickel (CuNi), also\nknown as Constantan\nRange: 0°C-600°C (32°F-1112°F)\nColors according to DIN EN 43714',
        name: 'Type U = Cu-CuNi',
      },
      temp1: 'Temp.1',
      temp2: 'Temp.2',
      temperature: 'Temperatur',
      total: 'Total',
      totalFlow: 'Total Flow',
      v: 'V',
      voltage: 'Voltage',
      weight1: 'Weight₁',
      weight2: 'Weight₂',
    },
    metricUnits: 'metrisch',
    mustBeInteger: 'Muss ganze Zahl sein',
    outOfRange: 'Außerhalb des zulässigen Wertebereichs',
    potentials: 'Potenziale',
    range: 'Bereich',
    units: 'Einheiten',
  },
  validation: {
    required: 'Pflichtfeld',
    tooBigFile: 'Die Dateigröße sollte weniger als 50 MB betragen',
  },
  util: {
    schaefflerChart: {
      CrEquivalent: 'Cr-Äquivalent',
      NiEquivalent: 'Ni-Äquivalent',
    },
  },
  useImportJsonValidated: {
    unknownFileValidationError: 'Datei konnte nicht validiert werden. Das schema der Daten entspricht nicht den Erwartungen.',
  },
  tenantSettings: {
    dataSources: {
      headline: 'Datenquellen',
      subheadline: 'Auf dieser Seite werden die Datenquellen von Qmulus verwaltet.',
    },
    license: {
      headline: 'Lizenzschlüssel',
      subheadline: 'Auf dieser Seite kann der Lizenzschlüssel von Qmulus aktualisiert werden.',
    },
    auditTrail: {
      headline: 'Prüfpfad',
      subheadline: 'Der Prüfpfad erfasst jede Änderung in der Anwendung und ordnet sie dem verantwortlichen Benutzer zu, um eine lückenlose Nachverfolgung aller Aktivitäten zu gewährleisten.',
    },
  },
  unitSystem,
  ...getComponentScopedTranslationMap('page', 'de'),
  ...getComponentScopedTranslationMap('components', 'de'),
  ...getComponentScopedTranslationMap('pt9800', 'de'),
};
